import { uniqueId } from 'lodash';
import { useState } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Form, FormField } from 'core/components';
import { NotificationElement } from 'core/components/Notification';
import { ApiStatus, InputType, NotificationPosition } from 'core/enums';
import { FormProvider, useAgGridClientTableContext, useNotificationContext } from 'core/states';
import { useQuotePartActions } from 'quotes/hooks';
import { ICustomerQuotePart, IQuotePart } from 'quotes/types';
import { getActiveQuoteParts } from 'quotes/utils';
import { Size } from 'theme/enums';

import { addVariationSchema } from './schema';
import { Table } from './Table';
import { IAddVariationsForm } from './types';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  quotePart: ICustomerQuotePart;
}

export const AddVariation = ({ isOpen, onClose, quotePart }: IProps) => {
  const [hintOpen, setHintOpen] = useState(true);

  const { t } = useTranslation();
  const { clearNotificationTimeout } = useNotificationContext();
  const { rows, updateRows } = useAgGridClientTableContext();

  const { addPnbVariations } = useQuotePartActions();

  const defaultData = [
    {
      id: uniqueId(),
      length: null,
      quantity: 1,
    },
    {
      id: uniqueId(),
      length: null,
      quantity: 1,
    },
  ];

  const notification = {
    title: t('titleHint'),
    message: t('messageHintAddNewVariation'),
    type: ApiStatus.INFO,
    position: NotificationPosition.FORM,
  };

  const onSubmit =
    (reset: UseFormReset<IAddVariationsForm>) => async (data: IAddVariationsForm) => {
      const { success, quoteParts } = await addPnbVariations({
        quoteId: quotePart.quoteId,
        number: quotePart.partArr?.number,
        manufacturerId: quotePart.partArr?.manufacturerId,
        note: data.note,
        variations: data.variations.map((variation) => ({
          value: variation.length,
          quantity: variation.quantity,
        })),
      });

      if (!success) {
        return;
      }

      handleOnClose(reset);

      // timeout needed in order to slow down adding new items in the table because closing the
      // modal will update a state in the cell clicked so it prevents go to the bottom of the table
      setTimeout(() => {
        const addIndex = getActiveQuoteParts(rows as IQuotePart[]).length;

        updateRows({ add: quoteParts, addIndex }, { redrawRows: false, refreshCells: false });
      }, 1000);
    };

  const handleOnClose = (reset: UseFormReset<IAddVariationsForm>) => {
    reset();
    onClose();
    setHintOpen(true);

    clearNotificationTimeout();
  };

  return (
    <FormProvider schema={addVariationSchema()} defaultValues={{ variations: defaultData }}>
      <Form
        title={t('titleAddVariations')}
        size={Size.MD}
        isOpen={isOpen}
        onClose={handleOnClose}
        onSubmit={onSubmit}
        submitBtnLabel={t('buttonLabelAddVariations')}
        cancelBtnLabel={t('buttonLabelCancel')}
      >
        <NotificationElement
          {...notification}
          showNotification={hintOpen}
          onClose={() => setHintOpen(false)}
        />

        <Table />

        <FormField
          name="note"
          type={InputType.TEXT_EDITOR}
          inputProps={{ label: t('labelNotes'), placeholder: t('placeholderNotes') }}
        />
      </Form>
    </FormProvider>
  );
};
